import React from 'react';
import styles from './CampaignsList.module.css';
import { useCampaignListData } from './useCampaignListData';
import Skeleton from 'react-loading-skeleton';
import CampaignBar from './components/CampaignBar/CampaignBar';
import range from 'lodash/range';
import { mapCampaignsToRows } from './helpers';
import useUrlState from '../../../../hooks/useUrlState';
import SearchInput from '../../../../ui/SearchInput/SearchInput';
import useOnScroll from '../../../../hooks/useOnScroll';
import { showNotification } from '../../../../utils/showNotification';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import { Button, Select } from '@round/ui-kit';
import { CampaignStatus } from '@round/api';
import { campaignStatusOptions as baseStatusOptions } from '../../../../utils/constants';
import { StylesConfig } from 'react-select';
import Checkbox from '../../../../ui/DataEntry/Checkbox/Checkbox';
import cn from 'classnames';

const selectStyles: StylesConfig = {
    container: (provided) => ({
        ...provided,
        minWidth: '8rem',
        maxWidth: '17rem',
    }),
    control: (provided) => ({
        ...provided,
        height: '2.5rem',
    }),
    singleValue: (provided, props) => ({
        ...provided,
        height: '100%',
        lineHeight: '1.75rem',
        color: '#58698E',
        fontWeight: props.data.value === undefined ? 'bold' : 'normal',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0.25rem 1rem',
    }),
    option: (provided, props) => ({
        ...provided,
        color: '#58698E',
        fontWeight: props.value === undefined ? 'bold' : 'normal',
    }),
};

const CampaignsList = () => {
    const [urlState, setUrlState] = useUrlState<{
        search: string | undefined;
        status: CampaignStatus | undefined;
        my_campaigns: boolean | undefined;
    }>({ search: '' }, { my_campaigns: 'boolean' });
    const {
        campaigns,
        campaignsLoading,
        errorFetchingCampaigns,
        bookings,
        campaignsWithBookingsLoading,
        initialized,
        init,
        reset,
        loadNextPage,
        hasNextPage,
        page,
    } = useCampaignListData({ search: urlState.search, status: urlState.status, my_campaigns: urlState.my_campaigns });

    const campaignsListRows = mapCampaignsToRows(campaigns, bookings);

    const loadNextRows = () => {
        if (campaignsLoading || !hasNextPage || !initialized) {
            return;
        }
        loadNextPage().catch(() => showNotification('Could not load next page', 'error'));
    };

    const handleScroll = useOnScroll(loadNextRows);

    useAbortableEffect(
        (signal) => {
            if (!initialized) {
                init({ signal }).catch(() => showNotification('Could not load data', 'error'));
            }
        },
        [initialized, init]
    );

    const statusOptions = [{ value: undefined, label: 'All Campaigns' }, ...baseStatusOptions];

    const nextPageLoading = initialized && campaignsLoading;
    const bottomReached = initialized && page > 1 && !hasNextPage;
    const noCampaigns = initialized && !errorFetchingCampaigns && campaigns.length === 0;

    return (
        <div className={styles.container} onScroll={handleScroll}>
            <div className={styles.headerRow}>
                <SearchInput
                    value={urlState.search}
                    onChange={(value) => {
                        setUrlState({ search: value });
                        reset();
                    }}
                    className={styles.searchBar}
                    placeholder="Search campaigns"
                />
                <div className={styles.filtersWrapper}>
                    <Button
                        onClick={() => {
                            setUrlState({ my_campaigns: !urlState.my_campaigns });
                            reset();
                        }}
                        className={styles.filterButton}
                    >
                        <span>My Campaigns</span>
                        <Checkbox
                            value={!!urlState.my_campaigns}
                            className={cn(styles.checkbox, {
                                [styles.checked]: !!urlState.my_campaigns,
                            })}
                        />
                    </Button>
                    <div className={styles.filterWrapper}>
                        <Select
                            options={statusOptions}
                            value={statusOptions.find((option) => option.value === urlState.status) ?? null}
                            onChange={(option) => {
                                setUrlState({ status: option?.value });
                                reset();
                            }}
                            styles={selectStyles}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.list}>
                {campaignsListRows.map((row) => (
                    <CampaignBar
                        key={row.id}
                        campaignData={row}
                        loading={campaignsWithBookingsLoading.includes(row.id)}
                    />
                ))}
            </div>
            <div className={styles.list}>
                {(nextPageLoading || !initialized) &&
                    range(0, 4).map((i) => <Skeleton key={i} className={styles.barSkeleton} />)}
            </div>
            {bottomReached && (
                <div className={styles.bottomReachedBar}>
                    <h4>Looks like you've reached the bottom</h4>
                </div>
            )}
            {noCampaigns && (
                <div className={styles.bottomReachedBar}>
                    <h4>Looks like there's nothing here</h4>
                </div>
            )}
        </div>
    );
};

export default CampaignsList;
